/**
 * campaignTitle.js
 *
 * The short name the Workflow Checker leads with.
 *
 * The reviewer's ask (2026-08-03): the full campaign summary took about a third
 * of the panel and led with a paragraph nobody read — "what people are really
 * gonna care about is the steps that are below it". So the panel leads with a
 * name of at most five words, generated from what the user actually asked, and
 * the reviewer can rename it.
 *
 * This is display state and it stays in the browser. `plan.summary` is part of
 * the signed submission and is what the narrative and the refusal record are
 * built on, so a title is never a substitute for it and this module never
 * rewrites it. A user-typed name is user text on a masked surface: it must not
 * reach the Orchestrator or the narrative (#38), which is why the title lives
 * here in the panel layer and not on the plan.
 */

/** Her stated ceiling for the generated name. */
export const TITLE_WORD_LIMIT = 5;

// Politeness and lead-ins carry no meaning in a title. Stripped so a request
// that is short in substance is not spent on "could you please".
const LEAD_INS = [
    /^(?:hey|hi|hello|ok|okay|so|now|please)\b[\s,]*/i,
    /^(?:can|could|would|will)\s+you\b[\s,]*/i,
    /^i(?:'d|\s+would)\s+like\s+(?:you\s+)?to\b[\s,]*/i,
    /^i\s+(?:want|need)\s+(?:you\s+)?to\b[\s,]*/i,
    /^(?:let'?s|lets)\b[\s,]*/i,
    /^(?:gibbs)\b[\s,:]*/i,
];

// "run a hit-to-lead campaign" is the campaign, not an instruction about it.
// The article is optional so "do lead optimization" loses its verb too.
const IMPERATIVE = /^(?:run|start|begin|do|perform|execute|kick\s+off|set\s+up)\s+(?:(?:a|an|the)\s+)?/i;

function stripLeadIns(text) {
    let out = text;
    let previous = null;
    // A request can stack them — "hey Gibbs, could you please ...".
    while (previous !== out) {
        previous = out;
        for (let i = 0; i < LEAD_INS.length; i += 1) {
            out = out.replace(LEAD_INS[i], '');
        }
    }
    return out.replace(IMPERATIVE, '');
}

function capitalizeFirst(text) {
    if (!text) return text;
    // Only the first character. The rest is the user's own casing, so
    // "SIRT-2 hit discovery" keeps its identifier intact.
    return text[0].toUpperCase() + text.slice(1);
}

function words(text) {
    return String(text || '').trim().split(/\s+/).filter(Boolean);
}

/**
 * A title from the user's own words, or null when their words cannot carry one.
 *
 * Deliberately all-or-nothing: truncating a long request at five words produces
 * a fragment ("Dock eligible undocked compounds minimize"), which reads worse
 * than the campaign's own name. Their phrasing is used only when the whole of it
 * already fits.
 */
export function titleFromRequest(request) {
    const cleaned = stripLeadIns(String(request || '').trim())
        .replace(/^["'`\s]+/, '')
        .replace(/["'`\s]+$/, '')
        .replace(/[.!?,;:]+$/, '')
        .trim();
    const parts = words(cleaned);
    if (parts.length === 0 || parts.length > TITLE_WORD_LIMIT) return null;
    return capitalizeFirst(parts.join(' '));
}

/**
 * A title from the plan itself, for when the request was a paragraph.
 *
 * The summary's leading clause is the campaign's name — "Hit-to-lead campaign:
 * dock eligible undocked compounds, ..." — and it is already short. Reading it
 * here does not change it.
 */
export function titleFromPlan(plan) {
    // A plan that already carries a name was named by a person — a saved
    // workflow, or a plan built from one. Used verbatim: the word ceiling
    // governs the name this module *generates*, and truncating someone's own
    // choice is not that.
    const named = typeof plan?.name === 'string' ? plan.name.trim() : '';
    if (named) return named;
    const summary = typeof plan?.summary === 'string' ? plan.summary.trim() : '';
    if (summary) {
        const lead = summary.split(':')[0].trim();
        const parts = words(lead);
        if (parts.length > 0 && parts.length <= TITLE_WORD_LIMIT) {
            return capitalizeFirst(parts.join(' '));
        }
        // No usable leading clause: keep the first five words rather than
        // inventing a name the plan does not support.
        if (parts.length > TITLE_WORD_LIMIT) {
            return capitalizeFirst(parts.slice(0, TITLE_WORD_LIMIT).join(' '));
        }
    }
    return plan?.executionMode === 'campaign' ? 'Campaign' : 'Workflow';
}

/**
 * The generated title for a freshly proposed plan.
 *
 * @param {Object} plan - the proposed plan; read, never modified
 * @param {string} [request] - what the user typed to get this plan
 * @returns {string} at most TITLE_WORD_LIMIT words
 */
export function generateCampaignTitle(plan, request) {
    return titleFromRequest(request) || titleFromPlan(plan);
}
